// src/api/workspacesApi.js - Refactored for new architecture
import { getCurrentUser } from '../components/utils/authService'; 

const API_BASE_URL = '/api';

const handleApiError = async (response, customMessage) => {
  if (!response.ok) {
    let errorMessage = customMessage;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      errorMessage = `${customMessage}: ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }
};

// ============================================================================
// WORKSPACE API (formerly top-level notes)
// ============================================================================

/**
 * Fetch all workspaces for the current user
 * @returns {Promise<Workspace[]>} Array of workspaces
 */
export const fetchWorkspaces = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    
    await handleApiError(response, 'Failed to fetch workspaces');
    return await response.json();
  } catch (error) {
    console.error('Error fetching workspaces:', error);
    throw error;
  }
};

/**
 * Fetch a specific workspace by ID
 * @param {string} publicId - The public ID of the workspace
 * @returns {Promise<Workspace>} The requested workspace
 */
export const fetchWorkspace = async (publicId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${publicId}`, {
      credentials: 'include'
    });
    
    await handleApiError(response, 'Failed to fetch workspace');
    return await response.json();
  } catch (error) {
    console.error('Error fetching workspace:', error);
    throw error;
  }
};

/**
 * Create a new workspace
 * @param {number} defaultCategoryId - The default category ID
 * @returns {Promise<Workspace>} The created workspace
 */
export const createWorkspace = async (defaultCategoryId) => {
  const newWorkspace = {
    name: 'Untitled',
    primary_category_id: defaultCategoryId,
    secondary_categories: '[]'
  };

  try {
    const response = await fetch(`${API_BASE_URL}/workspaces`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(newWorkspace)
    });

    if (!response.ok) {
      throw new Error('Failed to create workspace');
    }

    return await response.json();
  } catch (error) {
    console.error('Error in createWorkspace:', error);
    throw error;
  }
};

/**
 * Update an existing workspace
 * @param {string} publicId - The public ID of the workspace to update
 * @param {Object} workspaceData - The workspace data to update
 * @returns {Promise<Workspace>} The updated workspace
 */
export const updateWorkspace = async (publicId, workspaceData) => {
  try {
    const dataToSend = {
      name: workspaceData.name || workspaceData.title,
      primary_category_id: workspaceData.primary_category_id,
      secondary_categories: workspaceData.secondary_categories || '[]',
      preview_x: workspaceData.preview_x,
      preview_y: workspaceData.preview_y,
      preview_width: workspaceData.preview_width,
      preview_height: workspaceData.preview_height
    };
    
    console.log(`Sending update to server for workspace ${publicId}:`, dataToSend);
    
    const response = await fetch(`${API_BASE_URL}/workspaces/${publicId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dataToSend)
    });

    await handleApiError(response, 'Failed to update workspace');
    return await response.json();
  } catch (error) {
    console.error('Error updating workspace:', error);
    throw error;
  }
};

/**
 * Delete a workspace
 * @param {string} publicId - The public ID of the workspace to delete
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteWorkspace = async (publicId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${publicId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    await handleApiError(response, 'Failed to delete workspace');
    return true;
  } catch (error) {
    console.error('Error deleting workspace:', error);
    throw error;
  }
};

/**
 * Fetch recent workspaces
 * @param {number} limit - Maximum number of workspaces to return
 * @returns {Promise<Workspace[]>} Array of recent workspaces
 */
export const fetchRecentWorkspaces = async (limit = 20) => {
  const user = getCurrentUser();
  const response = await fetch(
    `/api/users/${user.user_id}/recent-workspaces?limit=${limit}`, 
    {
      method: 'GET',
      credentials: 'include',
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch recent workspaces');
  }
  
  return response.json();
};

// ============================================================================
// DOCUMENT API (formerly notes/subnotes)
// ============================================================================

/**
 * Fetch all documents in a workspace
 * @param {string} workspacePublicId - The public ID of the workspace
 * @returns {Promise<Document[]>} Array of documents
 */
export const fetchDocuments = async (workspacePublicId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/documents`, {
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    
    await handleApiError(response, 'Failed to fetch documents');
    const data = await response.json();
    return data.documents || [];
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

/**
 * Fetch a specific document by ID
 * @param {string} publicId - The public ID of the document
 * @returns {Promise<Document>} The requested document
 */
export const fetchDocument = async (publicId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/documents/${publicId}`, {
      credentials: 'include'
    });
    
    await handleApiError(response, 'Failed to fetch document');
    return await response.json();
  } catch (error) {
    console.error('Error fetching document:', error);
    throw error;
  }
};

/**
 * Create a new document in a workspace
 * @param {string} workspacePublicId - The public ID of the workspace
 * @param {string} userId - User ID (for compatibility)
 * @param {number} defaultCategoryId - Default category (not used for documents)
 * @returns {Promise<Document>} The created document
 */
export const createDocument = async (workspacePublicId, userId = null, defaultCategoryId = null) => {
  const newDocument = {
    title: 'New Document',
    content: '',
    tags: '',
    document_type: 'document'
  };

  console.log("Creating document with data:", JSON.stringify(newDocument, null, 2));
  
  try {
    const response = await fetch(`${API_BASE_URL}/workspaces/${workspacePublicId}/documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(newDocument)
    });

    await handleApiError(response, 'Failed to create document');
    return await response.json();
  } catch (error) {
    console.error('Error in createDocument:', error);
    throw error;
  }
};

/**
 * Update an existing document
 * @param {string} publicId - The public ID of the document to update
 * @param {Object} documentData - The document data to update
 * @returns {Promise<Document>} The updated document
 */
export const updateDocument = async (publicId, documentData) => {
  try {
    const dataToSend = {
      title: documentData.title,
      content: documentData.content || documentData.note,  // Support both content and note
      tags: documentData.tags || '',
      preview_x: documentData.preview_x,
      preview_y: documentData.preview_y,
      preview_width: documentData.preview_width,
      preview_height: documentData.preview_height
    };
    
    console.log(`Sending update to server for document ${publicId}:`, dataToSend);
    
    const response = await fetch(`${API_BASE_URL}/documents/${publicId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dataToSend)
    });

    await handleApiError(response, 'Failed to update document');
    return await response.json();
  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
};

/**
 * Delete a document
 * @param {string} publicId - The public ID of the document to delete
 * @returns {Promise<boolean>} True if deletion was successful
 */
export const deleteDocument = async (publicId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/documents/${publicId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    await handleApiError(response, 'Failed to delete document');
    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

// ============================================================================
// BACKWARD COMPATIBILITY ALIASES
// ============================================================================
// These allow existing code to keep working while you migrate

/**
 * @deprecated Use fetchWorkspaces instead
 * Fetch all notes for the current user
 */
export const fetchNotes = fetchWorkspaces;

/**
 * @deprecated Use fetchWorkspace instead
 * Fetch a specific note by ID
 */
export const fetchNote = fetchWorkspace;

/**
 * @deprecated Use createWorkspace instead
 * Create a new note
 */
export const createNote = async (userId, defaultCategoryId) => {
  return createWorkspace(defaultCategoryId);
};

/**
 * @deprecated Use updateWorkspace instead
 * Update an existing note
 */
export const updateNote = updateWorkspace;

/**
 * @deprecated Use deleteWorkspace instead
 * Delete a note
 */
export const deleteNote = deleteWorkspace;

/**
 * @deprecated Use fetchRecentWorkspaces instead
 * Fetch recent notes
 */
export const fetchRecentNotes = fetchRecentWorkspaces;

/**
 * @deprecated Use fetchDocuments instead
 * Fetch all subnotes for a specific parent note
 */
export const fetchSubnotes = fetchDocuments;

/**
 * @deprecated Use createDocument instead
 * Create a new subnote under a parent note
 */
export const createSubnote = createDocument;

/**
 * @deprecated Use fetchDocument instead
 * Fetch the content of a specific note
 */
export const fetchNoteContent = fetchDocument;

/**
 * @deprecated Use updateDocument instead
 * Update note preview position
 */
export const updateNotePreviewPosition = async (publicId, positionData) => {
  // Convert old position data format to new document format
  return updateDocument(publicId, positionData);
};
